import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { learningPathService, LearningPathEnrollment } from '../../services/learningPathService';
import MaterialSymbol from '../common/MaterialSymbol';

interface LearningPathsWidgetProps {
    limit?: number;
    onPathSelect?: (pathId: number) => void;
    onViewAll?: () => void;
}

/**
 * "Meine Lernpfade" — Dashboard-Widget im neuen Design (.pathrow).
 * Re-Skin von components/LearningPaths/MyLearningPaths: gleicher Datenabruf
 * (learningPathService.getActiveEnrollments), gleiche Props/Navigation.
 */
const LearningPathsWidget: React.FC<LearningPathsWidgetProps> = ({
    limit = 3,
    onPathSelect,
    onViewAll,
}) => {
    const translate = useTranslate();
    const [enrollments, setEnrollments] = useState<LearningPathEnrollment[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadEnrollments = async () => {
            try {
                setLoading(true);
                const data = await learningPathService.getActiveEnrollments();
                setEnrollments(data.slice(0, limit));
            } catch (err) {
                console.error('Failed to load learning path enrollments:', err);
            } finally {
                setLoading(false);
            }
        };
        loadEnrollments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="panel">
                <div className="skel" />
                <div className="skel" />
                <div className="skel" />
            </div>
        );
    }

    return (
        <div className="panel">
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className="ico"><MaterialSymbol name="route" size={19} /></span>
                    {translate('learningPaths.myPaths', { _: 'Meine Lernpfade' })}
                </h2>
                {onViewAll && (
                    <button type="button" className="seeall" onClick={onViewAll}>
                        {translate('common.viewAll', { _: 'Alle anzeigen' })}
                        <MaterialSymbol name="arrow_forward" size={15} />
                    </button>
                )}
            </div>

            {enrollments.length === 0 ? (
                <div className="empty-note">
                    <p>{translate('learningPaths.noEnrollments', { _: 'Keine aktiven Lernpfade' })}</p>
                    <p>{translate('learningPaths.exploreDescription', { _: 'Entdecke Lernpfade und starte deine Lernreise' })}</p>
                    {onViewAll && (
                        <button type="button" className="btn-out" onClick={onViewAll}>
                            {translate('learningPaths.explore', { _: 'Lernpfade entdecken' })}
                        </button>
                    )}
                </div>
            ) : (
                enrollments.map((enrollment) => {
                    const path = enrollment.learningPath;
                    const progress = parseFloat(enrollment.progressPercentage);
                    const isCompleted = enrollment.status === 'completed';

                    return (
                        <div
                            key={enrollment.id}
                            className="pathrow"
                            onClick={() => path && onPathSelect?.(path.id)}
                        >
                            <div className="top">
                                <div>
                                    <div className="name">{path?.title}</div>
                                    <div className="state">{learningPathService.getStatusLabel(enrollment.status)}</div>
                                </div>
                                <span className="go">
                                    <MaterialSymbol name={isCompleted ? 'check_circle' : 'arrow_forward'} size={20} />
                                </span>
                            </div>
                            <div className="bar"><i style={{ width: `${Math.max(progress, 2)}%` }} /></div>
                            <div className="pct">{learningPathService.formatProgress(progress)}</div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default LearningPathsWidget;
